import React, { useState, useEffect } from "react";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";

const WillFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    testatorFullName: "",
    testatorFatherOrHusband: "",
    testatorAge: "",
    testatorGender: "",
    testatorOccupation: "",
    testatorContact: "",
    testatorAddress: "",
    willTitle: "",
    willTitleOther: "",
    willDescription: "",
    beneficiaryDetails: "",
    propertyDetails: "",
    executorDetails: "",
    guardianDetails: "",
    specialInstructions: "",
    witnessDetails: "",
    revocationClause: "",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Testator Details:
Full Name: ${formData.testatorFullName}
Father's/Husband's Name: ${formData.testatorFatherOrHusband}
Age: ${formData.testatorAge}
Gender: ${formData.testatorGender}
Occupation: ${formData.testatorOccupation}
Contact: ${formData.testatorContact}
Address: ${formData.testatorAddress}

📋 Will Details:
Title: ${formData.willTitleOther || formData.willTitle}
Description: ${formData.willDescription}
Beneficiary Details: ${formData.beneficiaryDetails}
Property Details: ${formData.propertyDetails}
Executor Details: ${formData.executorDetails}
Guardian Details: ${formData.guardianDetails}
Special Instructions: ${formData.specialInstructions}
Witness Details: ${formData.witnessDetails}
Revocation Clause: ${formData.revocationClause}
`.trim();

    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:`;
      uploadedDocuments.forEach((doc, idx) => {
        detailsText += `\n\nDocument ${idx + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
  draftType: "will",
  entityName: formData.testatorFullName,
  uploadedDocuments,
  extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Testator Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="testatorFullName"
            value={formData.testatorFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="testatorFatherOrHusband"
            value={formData.testatorFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="testatorAge"
            value={formData.testatorAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="testatorGender"
            value={formData.testatorGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="testatorOccupation"
            value={formData.testatorOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="testatorContact"
            value={formData.testatorContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Address *</label>
          <textarea
            name="testatorAddress"
            value={formData.testatorAddress}
            onChange={handleInputChange}
            rows={3}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📋 Will Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Will Title *</label>
          <select
            name="willTitle"
            value={formData.willTitle}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Will Type</option>
            <option value="Last Will and Testament">Last Will and Testament</option>
            <option value="Simple Will">Simple Will</option>
            <option value="Living Will">Living Will</option>
            <option value="Joint Will">Joint Will</option>
            <option value="Mutual Will">Mutual Will</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.willTitle === "Other" && (
            <>
              <label>Specify Will Type *</label>
              <input
                type="text"
                name="willTitleOther"
                value={formData.willTitleOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Will Description *</label>
          <textarea
            name="willDescription"
            value={formData.willDescription}
            onChange={handleInputChange}
            rows={4}
            placeholder="Describe the purpose and overview of this will..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👥 Beneficiaries & Property
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Beneficiary Details *</label>
          <textarea
            name="beneficiaryDetails"
            value={formData.beneficiaryDetails}
            onChange={handleInputChange}
            rows={4}
            placeholder="List all beneficiaries with their full names, relationships, and what they will inherit..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Property Details *</label>
          <textarea
            name="propertyDetails"
            value={formData.propertyDetails}
            onChange={handleInputChange}
            rows={4}
            placeholder="Describe all assets, properties, bank accounts, investments to be distributed..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Executor Details *</label>
          <textarea
            name="executorDetails"
            value={formData.executorDetails}
            onChange={handleInputChange}
            rows={3}
            placeholder="Name and contact details of the person who will execute this will..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          ⚖️ Additional Details
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Guardian Details (if applicable)</label>
            <textarea
              name="guardianDetails"
              value={formData.guardianDetails}
              onChange={handleInputChange}
              rows={3}
              placeholder="If you have minor children, specify guardian details..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Special Instructions</label>
            <textarea
              name="specialInstructions"
              value={formData.specialInstructions}
              onChange={handleInputChange}
              rows={3}
              placeholder="Any special instructions for burial, funeral, or other wishes..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Witness Details *</label>
            <textarea
              name="witnessDetails"
              value={formData.witnessDetails}
              onChange={handleInputChange}
              rows={3}
              placeholder="Names and contact details of witnesses (minimum 2 required)..."
              required
            />
          </div>
        </div>
      </div>
      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I declare that this is my last will and testament and I revoke all previous wills *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">Create Will Document</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional will document</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          {currentStep === totalSteps && (
            <div className="form-page-section">
              <div className="form-page-section-title">📄 Supporting Documents</div>
              <p className="form-page-note">
                Upload property papers, prior wills, correspondence, or other supporting documents. Extracted text will be added to your will draft.
              </p>
              <DocumentUpload
                onDocumentsChange={handleDocumentsChange}
                maxFiles={8}
                acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              />
            </div>
          )}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Will Document
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WillFormPage;
